import { Flyout } from "@amzn/stencil-react-components/flyout";
import { useContext } from "react";

import {
  Chatbot,
  CHATBOT_TITLE_ELEMENT_ID,
} from "@/components/Chatbot/Chatbot";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

/**
 * Flyout panel for the AI onboarding chatbot.
 *
 * `Flyout` supplies the modal overlay, backdrop, focus trap, Esc handling and
 * `role="dialog"`. The titled header and close button come from the `ChatPanel`
 * that `Chatbot` renders.
 */
export const ChatbotFlyout = (): JSX.Element => {
  const { state, dispatch } = useContext(AppContext);

  const close = () => {
    dispatch({
      type: ActionType.SET_CHATBOT_FLYOUT_VISIBLE,
      chatbotFlyoutVisible: false,
    });
  };

  return (
    <Flyout
      isOpen={state.chatbotFlyoutVisible}
      close={close}
      // A stray click outside must not discard an in-progress conversation, so
      // the backdrop is not a dismiss affordance. Esc and the header close
      // button still dismiss the panel.
      shouldCloseOnClickOutside={false}
      /*
        Points at the panel's own title heading rather than Stencil's title
        wrapper, which also holds the header back button and would fold that
        button's label into the dialog's accessible name. `Flyout` forwards only
        aria-labelledby and aria-describedby, so aria-label is not an option.
      */
      aria-labelledby={CHATBOT_TITLE_ELEMENT_ID}
      dataTestId="chatbotFlyout"
    >
      <Chatbot onClose={close} />
    </Flyout>
  );
};
